import { getLocalTimeZone, isSameDay, isToday, today } from '@internationalized/date'
import { useControlledState } from '@react-stately/utils'
import { Calendar as CalendarIcon, ChevronDown, ChevronLeft, ChevronRight } from '@untitledui/icons'
import { useDateFormatter } from 'react-aria'
import {
  DatePicker as AriaDatePicker,
  Dialog as AriaDialog,
  Group as AriaGroup,
  Popover as AriaPopover
} from 'react-aria-components'

import Button, { type ButtonProps } from '@components/designSystem/Button'
import useTypedTranslation from '@hooks/useTypedTranslation.ts'
import { cx } from '@utils/cx'

import { Calendar, type CalendarProps } from './calendar'

import type { DatePickerProps as AriaDatePickerProps, DateValue } from 'react-aria-components'

const highlightedDates = [today(getLocalTimeZone())]

interface BaseDatePickerProps extends AriaDatePickerProps<DateValue>, Pick<CalendarProps, 'showInput' | 'preset'> {
}

interface DatepickerWithNextAndPreviousButtonsProps extends BaseDatePickerProps {
  prevButton: {
    ariaLabel?: string
    isDisabled?: boolean
    onClick: () => void
  }
  nextButton: {
    ariaLabel?: string
    isDisabled?: boolean
    onClick: () => void
  }
}

type DatePickerProps = BaseDatePickerProps | DatepickerWithNextAndPreviousButtonsProps

export const DatePicker = ({ value: valueProp, defaultValue, onChange, ...props }: DatePickerProps) => {
  const { t } = useTypedTranslation()
  const formatter = useDateFormatter({
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })

  const [value, setValue] = useControlledState(valueProp, defaultValue ?? null, onChange)

  function formatSelectedDate() {
    if (!value) {
      return t('common.datepicker.placeholder')
    }

    if (isToday(value, getLocalTimeZone())) {
      return t('common.datepicker.today')
    }

    const yesterday = today(getLocalTimeZone()).subtract({ days: 1 })

    if (isSameDay(value, yesterday)) {
      return t('common.datepicker.yesterday')
    }

    return formatter.format(value.toDate(getLocalTimeZone()))
  }

  return (
    <AriaGroup className='flex items-center gap-1'>
      { 'prevButton' in props &&
          <DatepickerButton
            iconLeading={ChevronLeft}
            aria-label={props.prevButton.ariaLabel}
            isDisabled={props.prevButton.isDisabled}
            onClick={props.prevButton.onClick}
          />
      }
      <AriaDatePicker shouldCloseOnSelect={false} {...props} value={value} onChange={setValue}>
        <AriaGroup>
          <DatepickerButton
            iconLeading={<CalendarIcon className="size-5 text-brand-400" />}
            iconTrailing={ChevronDown}
          >
            {formatSelectedDate()}
          </DatepickerButton>
        </AriaGroup>
        <AriaPopover
          offset={12}
          placement="bottom right"
          className={({ isEntering, isExiting }) =>
            cx(
              'origin-(--trigger-anchor-point) will-change-transform',
              isEntering &&
                            `
                              duration-150 ease-out animate-in fade-in
                              placement-right:slide-in-from-left-0.5
                              placement-top:slide-in-from-bottom-0.5
                              placement-bottom:slide-in-from-top-0.5
                            `,
              isExiting &&
                            `
                              duration-100 ease-in animate-out fade-out
                              placement-right:slide-out-to-left-0.5
                              placement-top:slide-out-to-bottom-0.5
                              placement-bottom:slide-out-to-top-0.5
                            `
            )
          }
        >
          <AriaDialog className="rounded-2xl bg-primary shadow-xl ring ring-secondary_alt">
            <div className="flex px-6 py-5">
              <Calendar highlightedDates={highlightedDates} showInput={props.showInput} preset={props.preset} />
            </div>
          </AriaDialog>
        </AriaPopover>
      </AriaDatePicker>
      { 'nextButton' in props &&
          <DatepickerButton
            iconLeading={ChevronRight}
            aria-label={props.nextButton.ariaLabel}
            isDisabled={props.nextButton.isDisabled}
            onClick={props.nextButton.onClick}
          />
      }
    </AriaGroup>
  )
}

function DatepickerButton({ className, ...props }: Omit<ButtonProps, 'size' | 'variant'>) {
  return (
    <Button
      className={cx('rounded-lg ring-border-secondary', className)}
      size="md"
      variant="outline"
      aria-label={props['aria-label']}
      {...props}
    />
  )
}